/**
 * prepWorkerPool — GAL-8 worker-backed wrapper around imagePrep.
 *
 * WHY: prepImageForUpload uses OffscreenCanvas, but it still ran on
 * the main thread — a 40-file batch pinned the UI while it chewed.
 * This pool moves the decode/encode into real Web Workers (up to 3,
 * matching imagePrep's own "more than ~3 just thrashes" guidance)
 * and falls back to the inline path wherever Workers or
 * OffscreenCanvas aren't available (old Safari, jsdom, SSR) or the
 * worker errors — prep must NEVER be the reason an upload dies.
 */
import { prepImageForUpload, type PreppedImage } from '@/lib/imagePrep';

interface Pending {
  resolve: (r: PreppedImage) => void;
}

interface PoolWorker {
  worker: Worker;
  busy: boolean;
}

const POOL_SIZE = 3;

let workers: PoolWorker[] | null = null;
let broken = false;
let seqCounter = 0;
const pending = new Map<number, Pending>();
const waitQueue: Array<() => void> = [];

const workersSupported = () =>
  typeof Worker === 'function' && typeof OffscreenCanvas === 'function';

function spawnPool(): PoolWorker[] | null {
  if (broken || !workersSupported()) return null;
  if (workers) return workers;
  try {
    workers = Array.from({ length: POOL_SIZE }, () => {
      const worker = new Worker(new URL('./imagePrep.worker.ts', import.meta.url), {
        type: 'module',
      });
      const entry: PoolWorker = { worker, busy: false };
      worker.onmessage = (e: MessageEvent<{ seq: number } & PreppedImage>) => {
        const { seq, file, width, height, recompressed } = e.data;
        entry.busy = false;
        pending.get(seq)?.resolve({ file, width, height, recompressed });
        pending.delete(seq);
        waitQueue.shift()?.();
      };
      // A crashed worker poisons the whole pool — flip to inline
      // prep for the rest of the session rather than losing files.
      worker.onerror = () => {
        broken = true;
      };
      return entry;
    });
    return workers;
  } catch {
    // Worker construction failed (CSP, packaging) — inline fallback.
    broken = true;
    return null;
  }
}

function idleWorker(): PoolWorker | undefined {
  return spawnPool()?.find((w) => !w.busy);
}

/**
 * Prep one image. Uses a pooled worker when possible; otherwise the
 * inline (main-thread) imagePrep — identical output either way.
 */
export async function prepImageInWorker(file: File): Promise<PreppedImage> {
  if (broken || !workersSupported()) return prepImageForUpload(file);

  // Wait for a free worker (the engine already bounds concurrency,
  // so this queue only fills when other callers share the pool).
  let entry = idleWorker();
  while (!entry) {
    if (broken) return prepImageForUpload(file);
    await new Promise<void>((r) => waitQueue.push(r));
    entry = idleWorker();
  }

  const seq = seqCounter++;
  entry.busy = true;
  return new Promise<PreppedImage>((resolve) => {
    pending.set(seq, { resolve });
    try {
      entry!.worker.postMessage({ seq, file });
    } catch {
      // postMessage refused (worker torn down) — inline fallback.
      pending.delete(seq);
      entry!.busy = false;
      broken = true;
      void prepImageForUpload(file).then(resolve);
    }
  });
}
